import { AggregationGroupByFieldArray } from '@monorepo/shared/componentsV2/aggregation/AggregationGroupByFieldArray/AggregationGroupByFieldArray';
import {
  AggregationDefinitionRequest,
  DatasetPropertyResponse,
  DatasetPropertyType,
  DatasetResponse,
} from 'mapistry-shared';
import React, { useCallback } from 'react';
import { DeepPartial } from 'utility-types';
import { FormValues } from './types';

const propertyTypesAvailableForGrouping = [
  DatasetPropertyType.BOOLEAN,
  DatasetPropertyType.RECORD,
  DatasetPropertyType.TEXT,
  DatasetPropertyType.USER,
];

/**
 * When record property is selected, we group by all of its children.
 * If neither of these children is a property that we can group by, we are not allowing to group by the record property.
 * For example, Period property that comes out of upstream aggregation (it has only date range children)
 */
function isRecordPropertyWithoutChildren(
  property: DatasetPropertyResponse,
  allProperties: DatasetPropertyResponse[],
) {
  if (property.type !== DatasetPropertyType.RECORD) {
    return false;
  }
  const nonRecordDescendants = allProperties.filter(
    (p) =>
      p.parentPropertyIdChain.includes(property.id) &&
      p.type !== DatasetPropertyType.RECORD,
  );
  return !nonRecordDescendants.some((p) =>
    propertyTypesAvailableForGrouping.includes(p.type),
  );
}

/**
 * Not allowing to group by the same property multiple times.
 * Grouping by its children or parents is redundant
 */
function excludeBasedOnGroupedProperties(
  allProperties: DatasetPropertyResponse[],
  groupedPropertyIdsFormValue: DeepPartial<string>[] = [],
) {
  const toExcludeSet = groupedPropertyIdsFormValue.reduce(
    (set, groupedPropertyId) => {
      if (!groupedPropertyId) {
        return set;
      }

      const groupedProperty = allProperties.find(
        (p) => p.id === groupedPropertyId,
      );
      if (!groupedProperty) {
        throw new Error(
          `Workflow aggregation step groups by non-existing field (id: "${groupedPropertyId}")`,
        );
      }
      const ancestorIds = groupedProperty.parentPropertyIdChain;
      const descendantIds = allProperties
        .filter((p) => p.parentPropertyIdChain.includes(groupedPropertyId))
        .map((p) => p.id);

      return new Set([
        ...set,
        groupedPropertyId,
        ...ancestorIds,
        ...descendantIds,
      ]);
    },
    new Set<string>(),
  );

  return [...toExcludeSet];
}

/**
 * Not allowing to group by the same property as we used in aggregation, e.g. for count distinct we can choose any column type.
 * Also grouping by its parent is not allowed either since we will group by all the child properties under the hood.
 */
function excludeBasedOnAggregatedProperties(
  allProperties: DatasetPropertyResponse[],
  aggregationDefinitionsFormValue: DeepPartial<AggregationDefinitionRequest>[] = [],
) {
  const toExcludeSet = aggregationDefinitionsFormValue.reduce(
    (set, aggregationDefinition) => {
      const aggregatedPropertyId = aggregationDefinition.propertyId;
      if (!aggregatedPropertyId) {
        return set;
      }

      const aggregatedProperty = allProperties.find(
        (p) => p.id === aggregatedPropertyId,
      );
      if (!aggregatedProperty) {
        throw new Error(
          `Workflow step aggregates non-existing field (id: "${aggregatedPropertyId}")`,
        );
      }
      const ancestorIds = aggregatedProperty.parentPropertyIdChain;
      return new Set([...set, aggregatedPropertyId, ...ancestorIds]);
    },
    new Set<string>(),
  );

  return [...toExcludeSet];
}

function getPropertyIdsToExclude(
  allProperties: DatasetPropertyResponse[],
  groupedPropertyIdsFormValue?: DeepPartial<string>[],
  aggregationDefinitionsFormValue?: DeepPartial<AggregationDefinitionRequest>[],
) {
  const toExcludeSet = new Set([
    ...excludeBasedOnGroupedProperties(
      allProperties,
      groupedPropertyIdsFormValue,
    ),
    ...excludeBasedOnAggregatedProperties(
      allProperties,
      aggregationDefinitionsFormValue,
    ),
  ]);
  return [...toExcludeSet];
}

interface AggregationGroupBySectionProps {
  stepInputDataset: DatasetResponse;
}

export function AggregationGroupByList({
  stepInputDataset,
}: AggregationGroupBySectionProps) {
  const getAllowedGroupByColumnOptions = useCallback(
    (formValues: DeepPartial<FormValues>, fieldIndex?: number) => {
      const propertyIdsToExclude = getPropertyIdsToExclude(
        stepInputDataset.schemaProperties,
        formValues.groupedPropertyIds,
        formValues.aggregationDefinitions,
      );
      const selectedGroupedPropertyIds = formValues.groupedPropertyIds || [];
      const selectedIdInTargetField =
        fieldIndex != null ? selectedGroupedPropertyIds[fieldIndex] : undefined;
      return stepInputDataset.schemaProperties
        .filter((p) => {
          // if a property is already selected in the dropdown, then it is always allowed
          if (selectedIdInTargetField === p.id) return true;

          return (
            !p.isHidden &&
            propertyTypesAvailableForGrouping.includes(p.type) &&
            !isRecordPropertyWithoutChildren(
              p,
              stepInputDataset.schemaProperties,
            ) &&
            !propertyIdsToExclude.includes(p.id)
          );
        })
        .map((p) => ({
          label: p.fullName,
          value: p.id,
        }));
    },
    [stepInputDataset],
  );

  return (
    <AggregationGroupByFieldArray<FormValues>
      fieldName="groupedPropertyIds"
      getAllowedGroupByColumnOptions={getAllowedGroupByColumnOptions}
    />
  );
}
